/**
 * <s-carousel> — a horizontally-scrollable "swim lane" that shows multiple items at once.
 *
 * UI-only: it wires the optional prev/next buttons to scroll the track by roughly one page
 * and toggles their `disabled` state at the start/end of the scroll range. The track is a
 * native scroll-snap container, so scrolling, touch/trackpad swiping, and keyboard focus
 * all work without JS — this component only enhances the arrow controls.
 *
 * Expected DOM (rendered by blocks/carousel.liquid):
 *   <s-carousel>
 *     [<button data-carousel-prev> / <button data-carousel-next>]
 *     <div data-carousel-track> …items… </div>
 *   </s-carousel>
 */
class SCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector("[data-carousel-track]");
    this.prevButton = this.querySelector("[data-carousel-prev]");
    this.nextButton = this.querySelector("[data-carousel-next]");

    if (!this.track) return;

    this.onPrev = () => this.scrollByPage(-1);
    this.onNext = () => this.scrollByPage(1);
    this.onScroll = () => this.updateControls();

    this.prevButton?.addEventListener("click", this.onPrev);
    this.nextButton?.addEventListener("click", this.onNext);
    this.track.addEventListener("scroll", this.onScroll, { passive: true });

    this.resizeObserver = new ResizeObserver(() => this.updateControls());
    this.resizeObserver.observe(this.track);

    this.updateControls();
    this.setupAutoplay();
  }

  /**
   * Optional auto-advance (data-autoplay="<seconds>"): steps one item at a time and loops
   * back to the start. Pauses while hovered, focused or touched, when off screen, and never
   * runs for visitors who prefer reduced motion.
   */
  setupAutoplay() {
    const seconds = Number(this.dataset.autoplay);
    if (!seconds || window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    this.paused = false;
    this.visible = true;
    const pause = () => { this.paused = true; };
    const resume = () => { this.paused = false; };
    this.autoplayListeners = [
      ["pointerenter", pause], ["pointerleave", resume],
      ["focusin", pause], ["focusout", resume],
      ["touchstart", pause], ["touchend", resume],
    ];
    this.autoplayListeners.forEach(([type, fn]) => this.addEventListener(type, fn, { passive: true }));

    this.intersectionObserver = new IntersectionObserver(([entry]) => { this.visible = entry.isIntersecting; });
    this.intersectionObserver.observe(this);

    this.autoplayTimer = window.setInterval(() => {
      if (this.paused || !this.visible || document.hidden) return;
      const maxScroll = this.track.scrollWidth - this.track.clientWidth;
      if (maxScroll <= 1) return;
      if (this.track.scrollLeft >= maxScroll - 1) {
        this.track.scrollTo({ left: 0, behavior: "smooth" });
        return;
      }
      const item = this.track.firstElementChild;
      const step = item ? item.getBoundingClientRect().width : this.track.clientWidth * 0.8;
      this.track.scrollBy({ left: step, behavior: "smooth" });
    }, seconds * 1000);
  }

  disconnectedCallback() {
    this.prevButton?.removeEventListener("click", this.onPrev);
    this.nextButton?.removeEventListener("click", this.onNext);
    this.track?.removeEventListener("scroll", this.onScroll);
    this.resizeObserver?.disconnect();
    window.clearInterval(this.autoplayTimer);
    this.intersectionObserver?.disconnect();
    this.autoplayListeners?.forEach(([type, fn]) => this.removeEventListener(type, fn));
  }

  /**
   * Scroll the track by ~80% of its visible width in the given direction.
   * @param {number} direction -1 for previous, 1 for next.
   */
  scrollByPage(direction) {
    const amount = this.track.clientWidth * 0.8 * direction;
    this.track.scrollBy({ left: amount, behavior: "smooth" });
  }

  /** Disable prev/next when the track is at its respective end. */
  updateControls() {
    if (!this.prevButton && !this.nextButton) return;

    const maxScroll = this.track.scrollWidth - this.track.clientWidth;
    const atStart = this.track.scrollLeft <= 1;
    const atEnd = this.track.scrollLeft >= maxScroll - 1;
    const overflowing = maxScroll > 1;

    if (this.prevButton) this.prevButton.disabled = !overflowing || atStart;
    if (this.nextButton) this.nextButton.disabled = !overflowing || atEnd;
  }
}

if (!customElements.get("s-carousel")) {
  customElements.define("s-carousel", SCarousel);
}
